import { GenericDropdownOption } from 'App.types';
import { creatorbase } from '@round/api';
import { postStatusLabels } from 'Modules/Plans/Posts/helpers';
import { Select } from '@round/whitelabel-ui';
import styles from './PostManualStatusSelect.module.css';
import cn from 'classnames';
import moment from 'moment';
import { StylesConfig } from 'react-select';
import { getStatusBadgeClassName } from 'Modules/Plans/Posts/helpers/getPostStatusClassName/getPostStatusClassName';
import { ReactComponent as BriefedIcon } from 'Modules/Advertising/InfluencerPlan/assets/statuses/Briefed.svg';
import { ReactComponent as DraftApprovedIcon } from 'Modules/Advertising/InfluencerPlan/assets/statuses/DraftApproved.svg';
import { ReactComponent as DraftReceivedIcon } from 'Modules/Advertising/InfluencerPlan/assets/statuses/DraftReceived.svg';
import { ReactComponent as ExpectingDraftIcon } from 'Modules/Advertising/InfluencerPlan/assets/statuses/ExpectingDraft.svg';
import { ReactComponent as PlannedIcon } from 'Modules/Advertising/InfluencerPlan/assets/statuses/Planned.svg';
import { ReactComponent as RejectedDeclinedIcon } from 'Modules/Advertising/InfluencerPlan/assets/statuses/RejectedDeclined.svg';
import { ReactComponent as RedraftingIcon } from 'Modules/Advertising/InfluencerPlan/assets/statuses/Redrafting.svg';
import { ReactComponent as ToReplace } from 'Modules/Advertising/InfluencerPlan/assets/statuses/ToReplace.svg';

const icons: { [key in creatorbase.PostStatus]: typeof PlannedIcon | null } = {
    planned: PlannedIcon,
    briefed: BriefedIcon,
    draft_pending: ExpectingDraftIcon,
    draft_received: DraftReceivedIcon,
    redrafting: RedraftingIcon,
    draft_approved: DraftApprovedIcon,
    rejected_or_declined: RejectedDeclinedIcon,
    backup: null,
    live: null,
    to_replace: ToReplace,
};

const options: StatusOption[] = [
    { value: 'planned', label: postStatusLabels.planned, Icon: icons.planned },
    { value: 'briefed', label: postStatusLabels.briefed, Icon: icons.briefed },
    { value: 'draft_pending', label: postStatusLabels.draft_pending, Icon: icons.draft_pending },
    { value: 'draft_received', label: postStatusLabels.draft_received, Icon: icons.draft_received },
    { value: 'redrafting', label: postStatusLabels.redrafting, Icon: icons.redrafting },
    { value: 'draft_approved', label: postStatusLabels.draft_approved, Icon: icons.draft_approved },
    { value: 'to_replace', label: postStatusLabels.to_replace, Icon: icons.to_replace },
    { value: 'backup', label: postStatusLabels.backup, Icon: icons.backup },
    {
        value: 'rejected_or_declined',
        label: postStatusLabels.rejected_or_declined,
        Icon: icons.rejected_or_declined,
    },
];

// "Live" status is included to display as initial value for live posts
// even thought it's not an option on the dropdown
type StatusOption = GenericDropdownOption<creatorbase.PostStatus> & { Icon: typeof PlannedIcon | null };

const mapPostStatusToOption = (status: creatorbase.PostStatus): StatusOption => ({
    value: status,
    label: postStatusLabels[status],
    Icon: icons[status],
});

type Props = {
    value: creatorbase.PostStatus;
    onChange: (value: creatorbase.ManualStatus) => void;
    draftExpectedBy?: string | null;
    isDisabled?: boolean;
    isLoading?: boolean;
};

const PostManualStatusSelect = (props: Props) => {
    const selectStyles: StylesConfig = {
        container: (base) => ({
            ...base,
            width: 'fit-content',
        }),
        control: (base) => ({
            ...base,
            width: 'max-content',
            padding: '0.25rem',
            transition: 'none',
            background: 'transparent',
            border: '1px solid transparent',
            ':hover': {
                padding: '0.25rem',
                background: props.value === 'live' ? 'transparent' : base.background,
                border: props.value === 'live' ? '1px solid transparent' : base.border,
            },
        }),
        singleValue: (base) => ({
            ...base,
            position: 'static',
            top: '0',
            transform: 'none',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            display: props.value === 'live' || props.isDisabled ? 'none' : base.display,
        }),
        menu: (base) => ({
            ...base,
            width: '12.5rem',
        }),
    };

    return (
        <Select
            styles={selectStyles}
            menuPlacement="auto"
            isSearchable={false}
            isClearable={false}
            isDisabled={props.isDisabled}
            isLoading={props.isLoading}
            options={options}
            value={mapPostStatusToOption(props.value)}
            onChange={(selected) => {
                // check for "live" status is only for ts compiler
                // "live" option is removed on options computation
                if (selected && selected.value !== 'live') {
                    props.onChange(selected.value);
                }
            }}
            formatOptionLabel={(option, meta) => {
                if (meta.context === 'menu') {
                    const Icon = option.Icon;
                    return (
                        <div className={styles.menuOption}>
                            {Icon && <Icon className={styles.menuOptionIcon} />}
                            <span>{option.label}</span>
                        </div>
                    );
                }

                return (
                    <div className={styles.optionContainer}>
                        <span className={cn(styles.badge, getStatusBadgeClassName(option.value))}>{option.label}</span>
                        {props.draftExpectedBy && (
                            <span className={styles.expectedBy}>
                                {moment(props.draftExpectedBy).format('D MMM YYYY')}
                            </span>
                        )}
                    </div>
                );
            }}
        />
    );
};

export default PostManualStatusSelect;
